<template>
  <div class="leadList">
    <label class="leadHead">
      <input
        type="checkbox"
        :checked="allSelected"
        :indeterminate.prop="selectedCount > 0 && !allSelected"
        :disabled="disabled || !addableCount"
        @change="emit('toggle-all')"
      />
      <span>{{ addableCount ? `${selectedCount} of ${addableCount} selected` : 'Nothing new to add' }}</span>
    </label>
    <div class="leadScroll">
      <label v-for="lead in leads" :key="lead.lead_id" class="leadRow" :class="{ inQueue: lead.inQueue }">
        <input type="checkbox" :checked="lead.selected" :disabled="disabled || lead.inQueue" @change="emit('toggle-lead', lead.lead_id)" />
        <span class="leadText">
          <span class="leadName">{{ lead.name || 'Unnamed lead' }}</span>
          <span class="leadSub">{{ leadDetail(lead) }}</span>
        </span>
        <span v-if="lead.inQueue" class="leadWhen">In your queue</span>
        <span v-else-if="capturedLabel(lead.captured_at)" class="leadWhen">{{ capturedLabel(lead.captured_at) }}</span>
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { SelectableLead } from '../../composables/useCapturedLeads'
import { formatPhone } from '../../utils/phone'
import { capturedLabel } from '../../utils/recency'

const props = defineProps<{
  leads: SelectableLead[]
  selectedCount: number
  allSelected: boolean
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'toggle-lead', leadId: string): void
  (e: 'toggle-all'): void
}>()

const addableCount = computed(() => props.leads.filter((lead) => !lead.inQueue).length)

function leadDetail(lead: SelectableLead): string {
  return [lead.company, formatPhone(lead.phone_e164)].filter(Boolean).join(' · ')
}
</script>

<style scoped>
.leadList { display: flex; flex-direction: column; border: 1px solid var(--color-border-subtle); border-radius: var(--radius-md); overflow: hidden; }
.leadHead { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-2) var(--space-4); border-bottom: 1px solid var(--color-border-subtle); background: var(--color-bg-app); font-size: var(--fs-sm); font-weight: var(--fw-semibold); color: var(--color-text-secondary); cursor: pointer; }
.leadScroll { max-height: min(320px, 38vh); overflow-y: auto; overscroll-behavior: contain; }
.leadRow { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-2) var(--space-4); border-bottom: 1px solid var(--color-border-subtle); cursor: pointer; }
.leadRow:last-child { border-bottom: 0; }
.leadRow:hover { background: var(--color-bg-subtle); }
.leadRow.inQueue { cursor: default; }
.leadRow.inQueue:hover { background: transparent; }
.leadText { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.leadName { font-size: var(--fs-base); font-weight: var(--fw-semibold); color: var(--color-text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.leadRow.inQueue .leadName { font-weight: var(--fw-medium); color: var(--color-text-tertiary); }
.leadSub { font-size: var(--fs-xs); color: var(--color-text-tertiary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.leadWhen { flex-shrink: 0; font-size: var(--fs-xs); color: var(--color-text-tertiary); white-space: nowrap; }
</style>
